import { ExpMemberData } from 'core/api/client/MemberExperienceApi';
import { SITECORE_PAGES } from 'core/constants';

/***
 * Represents the structure of route data returned from Sitecore.
 */
export type RouteData = {
	sitecore?: {
		route?: {
			databaseName?: string;
			[key: string]: any;
		};
		context?: {
			[key: string]: any;
		};
		[key: string]: any;
	};
	[key: string]: any;
};

/***
 * Determines if the user should be forcibly logged out based on authentication and maintenance state.
 *
 * @param {string} accessToken - The user's access token.
 * @param {boolean} isUnauthenticated - Whether the user is unauthenticated.
 * @param {RouteData | any} routeData - The current route data or status code.
 * @returns {boolean} True if the user should be logged out, otherwise false.
 *
 * @example
 * shouldForceLogout('token', true, { sitecore: { context: { maintenance: { maintenanceFlag: true } } } });
 */
export function shouldForceLogout(
	accessToken: string,
	isUnauthenticated: boolean,
	routeData: RouteData | any,
): boolean {
	return (
		!!accessToken &&
		isUnauthenticated === true &&
		routeData === '302' &&
		routeData?.sitecore?.context?.maintenance?.maintenanceFlag === true
	);
}

/***
 * Determines if the user should be redirected to the login page.
 *
 * @param {string} accessToken - The user's access token.
 * @param {boolean} isUnauthenticated - Whether the user is unauthenticated.
 * @param {boolean} isWebBrowser - Whether the client is a web browser.
 * @param {boolean} isExperienceEditorActive - Whether the Sitecore Experience Editor is active.
 * @param {RouteData} routeData - The current route data.
 * @returns {boolean} True if the user should be redirected to login, otherwise false.
 *
 * @example
 * shouldRedirectToLogin('', true, true, false, { sitecore: { route: { databaseName: 'web' } } });
 */
export function shouldRedirectToLogin(
	accessToken: string,
	isUnauthenticated: boolean,
	isWebBrowser: boolean,
	isExperienceEditorActive: boolean,
	routeData: RouteData,
): boolean {
	return (
		!accessToken &&
		isUnauthenticated === true &&
		isWebBrowser &&
		!isExperienceEditorActive &&
		routeData?.sitecore?.route?.databaseName !== 'master'
	);
}

/***
 * Determines if the user should be redirected to an unauthorized page (401).
 *
 * @param {string} accessToken - The user's access token.
 * @param {RouteData | string} routeData - The current route data or status code.
 * @param {boolean} isUnauthenticated - Whether the user is unauthenticated.
 * @param {boolean} isWebBrowser - Whether the client is a web browser.
 * @param {boolean} isExperienceEditorActive - Whether the Sitecore Experience Editor is active.
 * @returns {boolean} True if the user should be redirected to unauthorized, otherwise false.
 *
 * @example
 * shouldRedirectToUnauthorized('token', '401', true, true, false);
 */
export function shouldRedirectToUnauthorized(
	accessToken: string,
	routeData: RouteData | string,
	isUnauthenticated: boolean,
	isWebBrowser: boolean,
	isExperienceEditorActive: boolean,
): boolean {
	return (
		!!accessToken &&
		routeData === '401' &&
		isUnauthenticated === true &&
		isWebBrowser &&
		!isExperienceEditorActive
	);
}

/***
 * Determines if the user should be redirected to a generic error page (500).
 *
 * @param {string} accessToken - The user's access token.
 * @param {RouteData | string} routeData - The current route data or status code.
 * @param {boolean} isUnauthenticated - Whether the user is unauthenticated.
 * @param {boolean} isWebBrowser - Whether the client is a web browser.
 * @param {boolean} isExperienceEditorActive - Whether the Sitecore Experience Editor is active.
 * @returns {boolean} True if the user should be redirected to error, otherwise false.
 *
 * @example
 * shouldRedirectToError('', '500', true, true, false);
 */
export function shouldRedirectToError(
	accessToken: string,
	routeData: RouteData | string,
	isUnauthenticated: boolean,
	isWebBrowser: boolean,
	isExperienceEditorActive: boolean,
): boolean {
	return (
		!accessToken &&
		routeData === '500' &&
		isUnauthenticated === true &&
		isWebBrowser &&
		!isExperienceEditorActive
	);
}

/***
 * Determines if the loading layout should be displayed based on fetching state and initial layout.
 *
 * @param {boolean} isFetching - Whether data is currently being fetched.
 * @param {() => string | null} getIsFirstSitecoreLayout - Function to get the first Sitecore layout state.
 * @returns {boolean} True if the loading layout should be shown, otherwise false.
 *
 * @example
 * shouldShowLoadingLayout(true, () => null);
 */
export function shouldShowLoadingLayout(
	isFetching: boolean,
	getIsFirstSitecoreLayout: () => string | null,
): boolean {
	const firstLayout = getIsFirstSitecoreLayout();

	return isFetching && (firstLayout === null || firstLayout === '');
}

/**
 * Merges memberApiResponse.data into routeData.sitecore.context.memberContext.memberData.
 * Returns a new routeData object.
 * @param {object} routeData - The original routeData object.
 * @param {object} memberApiResponse - The API response containing member data.
 * @returns {object} New routeData with merged memberData.
 */
export function mergeMemberDataIntoRouteData(
	routeData: RouteData,
	memberApiResponse: ExpMemberData,
) {
	if (!routeData || !routeData.sitecore || !routeData.sitecore.context) {
		return routeData;
	}
	const existingMemberContext =
		routeData?.sitecore?.context?.memberContext || {};
	const existingMemberData = existingMemberContext?.memberData || {};
	const apiMemberData = memberApiResponse?.data ?? {};

	return {
		...routeData,
		sitecore: {
			...routeData.sitecore,
			context: {
				...routeData.sitecore.context,
				memberContext: {
					...existingMemberContext,
					memberData: {
						...existingMemberData,
						...apiMemberData,
					},
				},
			},
		},
	};
}

/***
 * Determines if the user should be redirected to the outage dashboard after completes the login process.
 *
 * @param {boolean} isFailoverModeOn - Whether failover mode is active.
 * @returns {boolean} True if the user should be redirected to outage dashboard, otherwise false.
 *
 * @example
 * shouldRedirectToOutageDashboard(true);
 */
export function shouldRedirectToOutageDashboard(
	isFailoverModeOn: boolean,
): boolean {
	const currentUrl = window?.location?.pathname ?? SITECORE_PAGES.DASHBOARD;

	return isFailoverModeOn && currentUrl === SITECORE_PAGES.DASHBOARD;
}

/**
 * Redirects the user to the login page with a goto parameter for TAL refresh, preserving existing search parameters.
 * This is used when the user tries to access the TAL refresh page directly without authentication,
 * ensuring they are redirected back to the TAL refresh page after logging in.
 */
export function redirectTalRefreshToLogin() {
	if (window.location.search) {
		const searchParams = new URLSearchParams(window.location.search);
		const gotoParam = `goto=tal-refresh?${searchParams.toString()}`;
		window.location.assign(`${SITECORE_PAGES.LOGIN}?${gotoParam}`);
	}
}

/**
 * Returns a safe, internal path for post-login redirection.
 * Falls back to dashboard when `goto` is empty or unsafe.
 */
export function getSafeLoginRedirectPath(
	gotoParam: string | null | undefined,
	fallbackPath: string = SITECORE_PAGES.DASHBOARD,
): string {
	if (typeof gotoParam !== 'string') {
		return fallbackPath;
	}

	const trimmedGotoParam = gotoParam.trim();
	if (!trimmedGotoParam) {
		return fallbackPath;
	}

	if (/[\r\n]/.test(trimmedGotoParam)) {
		return fallbackPath;
	}

	if (
		trimmedGotoParam.startsWith('//') ||
		/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmedGotoParam)
	) {
		return fallbackPath;
	}

	try {
		const parsedGotoUrl = new URL(trimmedGotoParam, window.location.origin);

		if (parsedGotoUrl.origin !== window.location.origin) {
			return fallbackPath;
		}

		return `${parsedGotoUrl.pathname}${parsedGotoUrl.search}${parsedGotoUrl.hash}`;
	} catch {
		return fallbackPath;
	}
}
